/**
 * LaunchSetup — owner/admin onboarding gate, REAL validating wizard.
 *
 * Each step shows current state + lets the owner enter/edit/test before
 * confirming. The "Confirm and continue" button is disabled until the
 * underlying check has passed (server-side validated on submit).
 *
 * Steps:
 *   1. Business name      — text input, persists to system_settings
 *   2. Owner details      — name editable; email shown; verified-at shown
 *   3. Stripe probe       — live `account.retrieve` + `balance.retrieve`
 *   4. Email/domain test  — sends real Resend email, shows message id
 *   5. Final checklist    — auto-aggregated diagnostic, all-or-nothing
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  CheckCircle2, XCircle, Loader2, Building2, User as UserIcon,
  CreditCard, Mail, ClipboardCheck, ArrowRight, ArrowLeft, LogOut,
  ShieldCheck, Lock, RefreshCw, Send,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";

const API = process.env.REACT_APP_BACKEND_URL;

const STEPS = [
  { key: "business_name_confirmed", title: "Business / app name",   icon: Building2 },
  { key: "owner_confirmed",          title: "Owner / admin details", icon: UserIcon },
  { key: "payments_confirmed",       title: "Stripe / payment status", icon: CreditCard },
  { key: "email_domain_confirmed",   title: "Email / domain status", icon: Mail },
  { key: "checklist_confirmed",      title: "Final launch checklist", icon: ClipboardCheck },
];

const money = (p) => `£${(((p ?? 0)) / 100).toFixed(2)}`;

export default function LaunchSetup() {
  const navigate = useNavigate();
  const { logout, refreshUser } = useAuth();
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [stepIndex, setStepIndex] = useState(0);
  const [completing, setCompleting] = useState(false);

  async function loadState() {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/launch-setup/state`, { withCredentials: true });
      setState(data);
      // Auto-jump to first incomplete step
      const idx = STEPS.findIndex((s) => !data.state?.[s.key]);
      setStepIndex(idx === -1 ? STEPS.length - 1 : idx);
    } catch (e) {
      toast.error("Couldn't load setup", { description: e?.response?.data?.detail || e.message });
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { loadState(); }, []);

  const stepKey = STEPS[stepIndex].key;
  const isStepDone = (k) => !!state?.state?.[k];

  async function confirmAndAdvance() {
    try {
      await axios.put(`${API}/api/launch-setup/step`, { step: stepKey, confirmed: true }, { withCredentials: true });
      toast.success(`${STEPS[stepIndex].title} confirmed`);
      // Reload state then advance (or complete)
      await loadState();
      if (stepIndex < STEPS.length - 1) setStepIndex(stepIndex + 1);
    } catch (e) {
      toast.error("Cannot confirm yet", { description: e?.response?.data?.detail || e.message });
    }
  }

  async function complete() {
    setCompleting(true);
    try {
      await axios.post(`${API}/api/launch-setup/complete`, {}, { withCredentials: true });
      toast.success("Launch setup complete — admin dashboard unlocked.");
      if (refreshUser) await refreshUser();
      navigate("/admin", { replace: true });
    } catch (e) {
      toast.error("Couldn't finalise", { description: e?.response?.data?.detail || e.message });
    } finally {
      setCompleting(false);
    }
  }

  async function skipForNow() {
    if (!window.confirm(
      "Skip launch setup and open the admin dashboard?\n\n" +
      "Your wizard progress and probe results are preserved. " +
      "A 'Resume launch setup' pill will appear on /admin so you can finish later."
    )) return;
    setCompleting(true);
    try {
      await axios.post(`${API}/api/launch-setup/skip`, {}, { withCredentials: true });
      toast.success("Launch setup paused — opening dashboard.");
      if (refreshUser) await refreshUser();
      navigate("/admin", { replace: true });
    } catch (e) {
      toast.error("Couldn't skip", { description: e?.response?.data?.detail || e.message });
    } finally {
      setCompleting(false);
    }
  }

  const [probing, setProbing] = useState(false);
  const [probeResult, setProbeResult] = useState(null);

  async function probeEverything() {
    setProbing(true);
    setProbeResult(null);
    try {
      const { data } = await axios.post(
        `${API}/api/launch-setup/probe-everything`,
        {},
        { withCredentials: true }
      );
      setProbeResult(data.summary);
      if (data.summary?.all_passed) {
        toast.success("Everything passed — you're ready to confirm.");
      } else {
        const okCount = ["stripe_ok", "email_ok", "business_ok", "owner_ok"].filter(k => data.summary?.[k]).length;
        toast.message(`${okCount}/4 checks passed. Tap a step to fix the rest.`);
      }
      // Refresh wizard state so any auto-confirms surface
      await loadState();
    } catch (e) {
      toast.error("Probe failed", { description: e?.response?.data?.detail || e.message });
    } finally {
      setProbing(false);
    }
  }

  if (loading || !state) {
    return (
      <div className="min-h-dvh no-overflow-x flex items-center justify-center bg-slate-950">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="min-h-dvh no-overflow-x bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 text-slate-100">
      <div className="max-w-xl mx-auto px-4 sm:px-6 pt-8 pb-12">
        <div className="flex items-center justify-between mb-6">
          <div className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-amber-400">
            <Lock className="w-3.5 h-3.5" /> Launch Setup
          </div>
          <div className="flex items-center gap-2">
            <button
              data-testid="launch-setup-skip"
              onClick={skipForNow}
              disabled={completing}
              className="inline-flex items-center gap-1.5 text-xs font-semibold text-amber-300 hover:text-amber-200 min-h-[40px] px-3 rounded-lg ring-1 ring-amber-400/40 hover:ring-amber-300/60 bg-amber-400/5 hover:bg-amber-400/10 transition-colors disabled:opacity-50"
              title="Skip wizard and open admin dashboard. Progress preserved."
            >
              Skip for now <ArrowRight className="w-3.5 h-3.5" />
            </button>
            <button
              data-testid="launch-setup-logout"
              onClick={async () => { await logout(); navigate("/login"); }}
              className="inline-flex items-center gap-1 text-xs font-semibold text-slate-400 hover:text-white min-h-[40px] px-2"
            >
              <LogOut className="w-3.5 h-3.5" /> Sign out
            </button>
          </div>
        </div>

        <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight mb-2">
          Welcome back, {state.name || "owner"}.
        </h1>
        <p className="text-slate-400 text-sm sm:text-base mb-2">
          Five real checks. Each one validates against the live system before you can confirm.
        </p>
        <p className="text-slate-500 text-xs mb-6">
          Just here to review the platform? Tap <span className="text-amber-300 font-semibold">Skip for now</span> — your progress is saved and you can come back any time.
        </p>

        {/* Probe Everything Now — one-tap automated validation */}
        <div className="mb-6 rounded-2xl border border-emerald-500/30 bg-emerald-500/5 p-4">
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-xl bg-emerald-500/15 border border-emerald-500/40 flex items-center justify-center flex-shrink-0">
              <CheckCircle2 className="w-4 h-4 text-emerald-300" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-extrabold text-white">Probe everything now</p>
              <p className="text-xs text-slate-300 mt-0.5">
                One tap runs Stripe, Resend, business + owner checks, and refreshes the checklist. Anything that passes auto-confirms.
              </p>
            </div>
            <button
              type="button"
              data-testid="probe-everything-btn"
              onClick={probeEverything}
              disabled={probing || completing}
              className="inline-flex items-center gap-1.5 px-3.5 h-10 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-slate-900 text-xs font-bold disabled:opacity-50"
            >
              {probing ? "Running…" : "Run probe"}
            </button>
          </div>
          {probeResult && (
            <div data-testid="probe-everything-result" className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-2">
              {[
                ["stripe_ok", "Stripe"],
                ["email_ok", "Email"],
                ["business_ok", "Business"],
                ["owner_ok", "Owner"],
              ].map(([k, label]) => (
                <div
                  key={k}
                  className={`rounded-xl px-3 py-2 text-center ${
                    probeResult[k]
                      ? "bg-emerald-500/15 border border-emerald-500/40 text-emerald-200"
                      : "bg-rose-500/10 border border-rose-500/40 text-rose-200"
                  }`}
                >
                  <p className="text-[10px] uppercase tracking-widest font-bold opacity-80">{label}</p>
                  <p className="text-base font-extrabold">{probeResult[k] ? "✓" : "✗"}</p>
                </div>
              ))}
            </div>
          )}
          {probeResult?.auto_confirmed?.length > 0 && (
            <p className="mt-2 text-[11px] text-emerald-300">
              Auto-confirmed: {probeResult.auto_confirmed.map(k => k.replace("launch_setup.", "").replace("_confirmed", "")).join(" · ")}
            </p>
          )}
        </div>

        {/* Progress strip */}
        <div data-testid="launch-progress" className="flex gap-1 mb-4">
          {STEPS.map((s, i) => (
            <div
              key={s.key}
              data-testid={`progress-pill-${s.key}`}
              className={`flex-1 h-1.5 rounded-full transition-colors ${
                isStepDone(s.key) ? "bg-emerald-500" : i === stepIndex ? "bg-amber-400" : "bg-slate-700"
              }`}
            />
          ))}
        </div>
        <div className="flex items-center justify-between mb-4 text-[11px] uppercase tracking-widest">
          <span className="text-slate-500">Step {stepIndex + 1} of {STEPS.length}</span>
          <span data-testid="launch-progress-count" className="text-slate-400">{state.steps_done} / {state.steps_total}</span>
        </div>

        {/* Step card */}
        <div className="rounded-2xl ring-1 ring-slate-700 bg-slate-800/50 p-4 sm:p-5">
          {stepKey === "business_name_confirmed" && (
            <BusinessStep done={isStepDone(stepKey)} onConfirm={confirmAndAdvance} />
          )}
          {stepKey === "owner_confirmed" && (
            <OwnerStep done={isStepDone(stepKey)} onConfirm={confirmAndAdvance} />
          )}
          {stepKey === "payments_confirmed" && (
            <StripeStep done={isStepDone(stepKey)} onConfirm={confirmAndAdvance} />
          )}
          {stepKey === "email_domain_confirmed" && (
            <EmailStep done={isStepDone(stepKey)} onConfirm={confirmAndAdvance} ownerEmail={state.email} />
          )}
          {stepKey === "checklist_confirmed" && (
            <ChecklistStep done={isStepDone(stepKey)} onConfirm={confirmAndAdvance} />
          )}
        </div>

        {/* Step nav */}
        <div className="mt-4 flex items-center justify-between">
          <button
            data-testid="launch-back"
            onClick={() => setStepIndex(Math.max(0, stepIndex - 1))}
            disabled={stepIndex === 0}
            className="inline-flex items-center gap-1 min-h-[40px] px-2 text-xs font-semibold text-slate-400 hover:text-white disabled:opacity-30"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> Back
          </button>
          {stepIndex < STEPS.length - 1 && (
            <button
              data-testid="launch-skip-forward"
              onClick={() => setStepIndex(stepIndex + 1)}
              className="inline-flex items-center gap-1 min-h-[40px] px-2 text-xs font-semibold text-slate-400 hover:text-white"
            >
              Next <ArrowRight className="w-3.5 h-3.5" />
            </button>
          )}
        </div>

        {/* Final unlock */}
        <button
          data-testid="launch-complete-button"
          disabled={state.steps_done < state.steps_total || completing}
          onClick={complete}
          className={`mt-6 w-full inline-flex items-center justify-center gap-2 min-h-[52px] rounded-xl text-sm font-bold transition-colors ${
            state.steps_done >= state.steps_total
              ? "bg-amber-400 text-slate-950 hover:bg-amber-300"
              : "bg-slate-800 text-slate-500 ring-1 ring-slate-700 cursor-not-allowed"
          }`}
        >
          {completing ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
          {state.steps_done >= state.steps_total
            ? "Unlock admin dashboard"
            : `Finish ${state.steps_total - state.steps_done} more step${state.steps_total - state.steps_done === 1 ? "" : "s"}`}
          {state.steps_done >= state.steps_total && <ArrowRight className="w-4 h-4" />}
        </button>
      </div>
    </div>
  );
}


// ───────── Step 1: Business ─────────
function BusinessStep({ done, onConfirm }) {
  const [data, setData] = useState(null);
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);

  async function load() {
    const { data: d } = await axios.get(`${API}/api/launch-setup/business`, { withCredentials: true });
    setData(d);
    setName(d.name || "");
  }
  useEffect(() => { load(); }, []);

  async function save() {
    setSaving(true);
    try {
      await axios.put(`${API}/api/launch-setup/business`, { name }, { withCredentials: true });
      toast.success("Business name saved");
      await load();
    } catch (e) {
      toast.error("Save failed", { description: e?.response?.data?.detail || e.message });
    } finally {
      setSaving(false);
    }
  }

  const canConfirm = (data?.name || "").trim().length >= 2 && (data?.name || "").trim() === (name || "").trim();

  return (
    <StepShell title="Business / app name" icon={Building2} done={done} blurb="What learners and instructors will see in headers, emails and invoices.">
      <Field label="Business name" testid="business-name-input">
        <input
          data-testid="business-name-field"
          value={name}
          onChange={(e) => setName(e.target.value)}
          maxLength={80}
          className="w-full rounded-md bg-slate-900 ring-1 ring-slate-700 px-3 py-2 text-sm text-slate-100 focus:outline-none focus:ring-amber-400"
          placeholder="National Driving Guild"
        />
      </Field>

      {data && (
        <Row label="Saved value" value={data.name || "—"} testid="business-saved" ok={(data.name || "").length >= 2} />
      )}

      <div className="mt-3 flex gap-2 flex-wrap">
        <button
          data-testid="business-save-button"
          onClick={save}
          disabled={saving || (name || "").trim().length < 2}
          className="inline-flex items-center gap-1.5 min-h-[40px] px-3 rounded-md text-xs font-bold bg-slate-100 text-slate-900 hover:bg-white disabled:opacity-40"
        >
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
          Save
        </button>
        <ConfirmButton testid="business-confirm" canConfirm={canConfirm} done={done} onClick={onConfirm} />
      </div>
    </StepShell>
  );
}


// ───────── Step 2: Owner ─────────
function OwnerStep({ done, onConfirm }) {
  const [data, setData] = useState(null);
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);

  async function load() {
    const { data: d } = await axios.get(`${API}/api/launch-setup/owner`, { withCredentials: true });
    setData(d);
    setName(d.name || "");
  }
  useEffect(() => { load(); }, []);

  async function save() {
    setSaving(true);
    try {
      await axios.put(`${API}/api/launch-setup/owner`, { name }, { withCredentials: true });
      toast.success("Owner name saved");
      await load();
    } catch (e) {
      toast.error("Save failed", { description: e?.response?.data?.detail || e.message });
    } finally {
      setSaving(false);
    }
  }

  const canConfirm = (data?.name || "").trim().length >= 2 && (data?.name || "").trim() === (name || "").trim();

  return (
    <StepShell title="Owner / admin details" icon={UserIcon} done={done} blurb="Used on signed receipts and audit trails.">
      <Row label="Email of record" value={data?.email || "—"} testid="owner-email" ok={!!data?.email} />
      <Row label="Role" value={data?.role || "—"} testid="owner-role" ok={(data?.role || "").toLowerCase() === "admin"} />
      <Row
        label="Email verified"
        value={data?.email_verified_at ? new Date(data.email_verified_at).toLocaleDateString() : "Not verified"}
        testid="owner-verified"
        ok={!!data?.email_verified_at}
        warningOnly
      />
      <Field label="Owner name (editable)" testid="owner-name-input">
        <input
          data-testid="owner-name-field"
          value={name}
          onChange={(e) => setName(e.target.value)}
          maxLength={80}
          className="w-full rounded-md bg-slate-900 ring-1 ring-slate-700 px-3 py-2 text-sm text-slate-100 focus:outline-none focus:ring-amber-400"
        />
      </Field>
      <div className="mt-3 flex gap-2 flex-wrap">
        <button
          data-testid="owner-save-button"
          onClick={save}
          disabled={saving || (name || "").trim().length < 2}
          className="inline-flex items-center gap-1.5 min-h-[40px] px-3 rounded-md text-xs font-bold bg-slate-100 text-slate-900 hover:bg-white disabled:opacity-40"
        >
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
          Save name
        </button>
        <ConfirmButton testid="owner-confirm" canConfirm={canConfirm} done={done} onClick={onConfirm} />
      </div>
    </StepShell>
  );
}


// ───────── Step 3: Stripe probe ─────────
function StripeStep({ done, onConfirm }) {
  const [probe, setProbe] = useState(null);
  const [probing, setProbing] = useState(false);

  async function runProbe() {
    setProbing(true);
    try {
      const { data } = await axios.get(`${API}/api/launch-setup/stripe-probe`, { withCredentials: true });
      setProbe(data);
      if (data.ok) toast.success("Stripe probe passed");
      else toast.error("Stripe probe failed", { description: data.error });
    } catch (e) {
      toast.error("Stripe probe failed", { description: e?.response?.data?.detail || e.message });
    } finally {
      setProbing(false);
    }
  }

  const canConfirm = !!probe?.ok;

  return (
    <StepShell title="Stripe / payment status" icon={CreditCard} done={done} blurb="Live API call to Stripe — we'll fetch your account + balance to verify charges and payouts work.">
      <button
        data-testid="stripe-probe-button"
        onClick={runProbe}
        disabled={probing}
        className="inline-flex items-center gap-1.5 min-h-[40px] px-3 rounded-md text-xs font-bold bg-amber-400 text-slate-950 hover:bg-amber-300 disabled:opacity-60"
      >
        {probing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
        Run Stripe probe
      </button>

      {probe && (
        <div className="mt-3 space-y-1" data-testid="stripe-probe-result">
          {probe.ok ? (
            <>
              <Row label="Mode" value={probe.mode} ok={probe.mode === "live"} testid="stripe-mode" />
              <Row label="Account ID" value={probe.account_id} ok testid="stripe-account-id" />
              <Row label="Country" value={probe.country || "—"} ok testid="stripe-country" />
              <Row label="Charges enabled" value={probe.charges_enabled ? "yes" : "no"} ok={probe.charges_enabled} testid="stripe-charges" />
              <Row label="Payouts enabled" value={probe.payouts_enabled ? "yes" : "no"} ok={probe.payouts_enabled} testid="stripe-payouts" />
              <Row label="Available balance" value={money(probe.available_pence)} ok testid="stripe-available" />
              <Row label="Pending balance"   value={money(probe.pending_pence)}   ok testid="stripe-pending" />
            </>
          ) : (
            <Row label="Probe error" value={probe.error || "Unknown error"} ok={false} testid="stripe-error" />
          )}
        </div>
      )}

      <div className="mt-3">
        <ConfirmButton testid="stripe-confirm" canConfirm={canConfirm} done={done} onClick={onConfirm} />
      </div>
    </StepShell>
  );
}


// ───────── Step 4: Email/domain test ─────────
function EmailStep({ done, onConfirm, ownerEmail }) {
  const [status, setStatus] = useState(null);
  const [target, setTarget] = useState(ownerEmail || "");
  const [sending, setSending] = useState(false);

  async function load() {
    const { data } = await axios.get(`${API}/api/launch-setup/email-status`, { withCredentials: true });
    setStatus(data);
  }
  useEffect(() => { load(); }, []);
  useEffect(() => { if (ownerEmail && !target) setTarget(ownerEmail); }, [ownerEmail, target]);

  async function sendTest() {
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/api/launch-setup/email-test`, { to_email: target }, { withCredentials: true });
      if (data.ok) toast.success("Test email sent — check inbox", { description: `id ${data.message_id || "—"}` });
      else toast.error("Send failed", { description: data.error });
      await load();
    } catch (e) {
      toast.error("Send failed", { description: e?.response?.data?.detail || e.message });
    } finally {
      setSending(false);
    }
  }

  const probe = status?.last_probe;
  const canConfirm = !!probe?.ok;

  return (
    <StepShell title="Email / domain status" icon={Mail} done={done} blurb="Sends a real Resend email. The step only confirms after delivery returns ok.">
      <Row label="Sender" value={status?.sender || "—"} ok={!!status?.sender} testid="email-sender" />
      <Row
        label="Resend key"
        value={status?.resend_key_configured ? "configured" : "missing"}
        ok={!!status?.resend_key_configured}
        testid="email-key"
      />

      <Field label="Send test email to" testid="email-target-input">
        <input
          data-testid="email-target-field"
          type="email"
          value={target}
          onChange={(e) => setTarget(e.target.value)}
          className="w-full rounded-md bg-slate-900 ring-1 ring-slate-700 px-3 py-2 text-sm text-slate-100 focus:outline-none focus:ring-amber-400"
          placeholder="you@yourdomain.com"
        />
      </Field>

      <button
        data-testid="email-send-button"
        onClick={sendTest}
        disabled={sending || !target.includes("@")}
        className="inline-flex items-center gap-1.5 min-h-[40px] px-3 rounded-md text-xs font-bold bg-amber-400 text-slate-950 hover:bg-amber-300 disabled:opacity-60"
      >
        {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
        Send test email
      </button>

      {probe && (
        <div className="mt-3 space-y-1" data-testid="email-probe-result">
          <Row label="Last test" value={probe.to || "—"} ok={!!probe.ok} testid="email-last-to" />
          <Row label="Result" value={probe.ok ? "delivered to provider" : (probe.error || "failed")} ok={!!probe.ok} testid="email-last-result" />
          {probe.message_id && <Row label="Message ID" value={probe.message_id} ok testid="email-message-id" />}
          {probe.sent_at && <Row label="Sent at" value={new Date(probe.sent_at).toLocaleString()} ok testid="email-sent-at" />}
        </div>
      )}

      <div className="mt-3">
        <ConfirmButton testid="email-confirm" canConfirm={canConfirm} done={done} onClick={onConfirm} />
      </div>
    </StepShell>
  );
}


// ───────── Step 5: Checklist ─────────
function ChecklistStep({ done, onConfirm }) {
  const [data, setData] = useState(null);
  async function load() {
    const { data: d } = await axios.get(`${API}/api/launch-setup/checklist`, { withCredentials: true });
    setData(d);
  }
  useEffect(() => { load(); }, []);

  const canConfirm = !!data?.all_passed;

  return (
    <StepShell title="Final launch checklist" icon={ClipboardCheck} done={done} blurb="Auto-aggregated. All items must be green before this step confirms.">
      <button
        data-testid="checklist-refresh"
        onClick={load}
        className="inline-flex items-center gap-1.5 min-h-[40px] px-3 rounded-md text-xs font-bold bg-slate-100 text-slate-900 hover:bg-white"
      >
        <RefreshCw className="w-3.5 h-3.5" />
        Re-check
      </button>

      <ul className="mt-3 space-y-1" data-testid="checklist-items">
        {(data?.items || []).map((it) => (
          <li key={it.key}>
            <Row label={it.label} value={it.detail} ok={!!it.ok} testid={`checklist-${it.key}`} />
          </li>
        ))}
      </ul>

      <div className="mt-3">
        <ConfirmButton testid="checklist-confirm" canConfirm={canConfirm} done={done} onClick={onConfirm} />
      </div>
    </StepShell>
  );
}


// ───────── Shared atoms ─────────
function StepShell({ title, icon: Icon, done, blurb, children }) {
  return (
    <div>
      <div className="flex items-start gap-3 mb-3">
        <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-none ${done ? "bg-emerald-500/20 text-emerald-300" : "bg-slate-900 text-slate-300"}`}>
          {done ? <CheckCircle2 className="w-5 h-5" /> : <Icon className="w-5 h-5" />}
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-base font-bold text-slate-100">{title}</h3>
          <p className="text-xs text-slate-400 mt-0.5">{blurb}</p>
        </div>
      </div>
      {children}
    </div>
  );
}

function Field({ label, children, testid }) {
  return (
    <label className="block mt-3">
      <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-1" data-testid={testid}>{label}</div>
      {children}
    </label>
  );
}

function Row({ label, value, ok, warningOnly, testid }) {
  return (
    <div className="flex items-center justify-between gap-3 text-xs py-1" data-testid={testid}>
      <span className="text-slate-300 font-semibold">{label}</span>
      <span className={`inline-flex items-center gap-1 ${ok ? "text-emerald-400" : warningOnly ? "text-amber-400" : "text-red-400"}`}>
        {ok ? <CheckCircle2 className="w-3.5 h-3.5" /> : <XCircle className="w-3.5 h-3.5" />}
        <span className="text-slate-300 truncate max-w-[200px] sm:max-w-[260px]">{String(value ?? "—")}</span>
      </span>
    </div>
  );
}

function ConfirmButton({ canConfirm, done, onClick, testid }) {
  if (done) {
    return (
      <span data-testid={`${testid}-done`} className="inline-flex items-center gap-1.5 min-h-[40px] px-3 rounded-md text-xs font-bold bg-emerald-500/20 text-emerald-300 ring-1 ring-emerald-500/30">
        <CheckCircle2 className="w-3.5 h-3.5" /> Confirmed
      </span>
    );
  }
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      disabled={!canConfirm}
      className={`inline-flex items-center gap-1.5 min-h-[40px] px-3 rounded-md text-xs font-bold ${
        canConfirm
          ? "bg-emerald-500 text-white hover:bg-emerald-400"
          : "bg-slate-700 text-slate-500 cursor-not-allowed"
      }`}
    >
      Confirm and continue <ArrowRight className="w-3.5 h-3.5" />
    </button>
  );
}
